// API client functions for platform settings endpoints.
// Covers subscription info and tenant feature config.

import type { ApiClient } from '@/lib/api/client'

// --- Types ---

export interface BillingState {
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'unpaid'
  billing_interval: 'month' | 'year'
  api_access: boolean
  current_period_start: string
  current_period_end: string
  trial_end: string | null
  cancel_at: string | null
  grace_period_band: 0 | 1 | 2 | null
}

export interface SubscriptionInfo {
  tier_slug: string
  tier_display_name: string
  entitlements: Record<string, unknown>
  idp_provider_name: string | null
  idp_provider_type: string | null
  billing: BillingState | null
}

export interface TenantConfigToggles {
  confirmation_gates: boolean
  secondary_signoff: boolean
  assessment_overdue_notifications: boolean
  parent_child_risks: boolean
  compare_date_reporting: boolean
}

export interface TenantConfigResponse {
  toggles: TenantConfigToggles
  tier_entitlements: Record<string, unknown>
}

export type UpdateTenantConfigRequest = Partial<TenantConfigToggles>

// --- Toggle metadata for display ---

export interface ToggleMeta {
  key: keyof TenantConfigToggles
  /** Entitlement key in tier_entitlements that gates this toggle */
  entitlementKey: string
  labelKey: string
  descriptionKey: string
}

/** Ordered list of feature toggles with display metadata. */
export const TOGGLE_DEFINITIONS: ToggleMeta[] = [
  {
    key: 'assessment_overdue_notifications',
    entitlementKey: 'assessment_overdue_notifications',
    labelKey: 'platform:features.toggles.assessmentOverdueNotifications',
    descriptionKey: 'platform:features.toggles.assessmentOverdueNotificationsDesc',
  },
  {
    key: 'confirmation_gates',
    entitlementKey: 'confirmation_gates',
    labelKey: 'platform:features.toggles.confirmationGates',
    descriptionKey: 'platform:features.toggles.confirmationGatesDesc',
  },
  {
    key: 'secondary_signoff',
    entitlementKey: 'secondary_signoff',
    labelKey: 'platform:features.toggles.secondarySignoff',
    descriptionKey: 'platform:features.toggles.secondarySignoffDesc',
  },
  {
    key: 'parent_child_risks',
    entitlementKey: 'parent_child_risks',
    labelKey: 'platform:features.toggles.parentChildRisks',
    descriptionKey: 'platform:features.toggles.parentChildRisksDesc',
  },
  {
    key: 'compare_date_reporting',
    entitlementKey: 'compare_date_reporting',
    labelKey: 'platform:features.toggles.compareDateReporting',
    descriptionKey: 'platform:features.toggles.compareDateReportingDesc',
  },
]

// --- IdP types ---

export interface IdPProvider {
  id: string
  slug: string
  display_name: string
  requires_tenant_identifier: boolean
}

export interface SwitchIdPRequest {
  idp_provider_id: string
  client_id?: string
  client_secret?: string
  tenant_identifier?: string
}

export interface SwitchIdPResponse {
  auth_mode: 'oidc' | 'magic_link'
  users_pending_rebind: number
}

export interface RebindStatusResponse {
  total_users: number
  rebound: number
  pending: number
}

// --- API factory ---

export function platformApi(api: ApiClient) {
  return {
    getSubscription: (): Promise<SubscriptionInfo> =>
      api.get('/platform/subscription'),

    createCustomerPortalSession: (): Promise<{ portal_url: string }> =>
      api.post('/platform/subscription/customer-portal', {}),

    getTenantConfig: (): Promise<TenantConfigResponse> =>
      api.get('/platform/tenant-config'),

    updateTenantConfig: async (data: UpdateTenantConfigRequest): Promise<TenantConfigToggles> => {
      const resp: TenantConfigResponse = await api.put('/platform/tenant-config', data)
      return resp.toggles
    },

    getIdPProviders: (): Promise<IdPProvider[]> =>
      api.get('/platform/idp-providers'),

    switchIdP: (data: SwitchIdPRequest): Promise<SwitchIdPResponse> =>
      api.put('/platform/idp', data),

    getRebindStatus: (): Promise<RebindStatusResponse> =>
      api.get('/platform/idp/rebind-status'),
  }
}
